import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ClientService } from 'src/app/private/services/client.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

export interface PrescriptionTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  tag: string;
  tagColor: string;
  accentColor: string;
  headerType: 'text1' | 'text2' | 'image';
  enableLogo: boolean;
  enableWatermark: boolean;
  watermarkText: string;
  watermarkOpacity: number;
  enableFooter: boolean;
  footerText: string;
  mockupStyle: string; // css class
}

@Component({
  selector: 'app-prescription-config',
  templateUrl: './prescription-config.component.html',
  styleUrls: ['./prescription-config.component.scss']
})
export class PrescriptionConfigComponent implements OnInit {

  activeTab: 'custom' | 'templates' = 'custom';

  // Print Layout
  enablePrintLayout: boolean = false;
  prescriptionHeaderType: string = 'image'; // 'image' | 'text1' | 'text2'
  prescriptionAccentColor: string = '#0d4259';

  // Text Header Fields
  presc_doc_name: string = '';
  presc_doc_degree: string = '';
  presc_doc_reg: string = '';
  presc_clinic_name: string = '';
  presc_clinic_address: string = '';
  presc_clinic_phone: string = '';

  // Custom Image Header base64
  headerImageBase64: string = '';

  // Hospital Logo Settings
  enablePrescriptionLogo: boolean = false;
  prescriptionLogoBase64: string = '';

  // Watermark
  enablePrescriptionWatermark: boolean = false;
  prescriptionWatermarkText: string = '';
  prescriptionWatermarkOpacity: number = 0.08;

  // Footer
  enablePrescriptionFooter: boolean = false;
  prescriptionFooterText: string = '';

  // Applied template tracking
  appliedTemplateId: string | null = null;
  templateSearchQuery: string = '';
  selectedCategory: string = 'All';

  readonly categories = ['All', 'Classic', 'Modern', 'Minimal', 'Premium', 'Pediatric'];

  readonly templateLibrary: PrescriptionTemplate[] = [
    {
      id: 'classic-blue',
      name: 'Classic Blue',
      description: 'Traditional two-column layout with clinic name left and contact details right. A clean, professional look suited for general practitioners.',
      category: 'Classic',
      tag: 'Popular',
      tagColor: '#0d4259',
      accentColor: '#0d4259',
      headerType: 'text1',
      enableLogo: true,
      enableWatermark: false,
      watermarkText: '',
      watermarkOpacity: 0.08,
      enableFooter: true,
      footerText: 'For emergencies please call the clinic. Valid for 30 days.',
      mockupStyle: 'classic-blue'
    },
    {
      id: 'modern-teal',
      name: 'Modern Teal',
      description: 'Bold horizontal banner with a thick colored rule dividing the branding area. Eye-catching and modern for multi-specialty clinics.',
      category: 'Modern',
      tag: 'Trending',
      tagColor: '#0f766e',
      accentColor: '#0f766e',
      headerType: 'text2',
      enableLogo: true,
      enableWatermark: true,
      watermarkText: 'PRESCRIPTION',
      watermarkOpacity: 0.05,
      enableFooter: true,
      footerText: 'This prescription is valid for 15 days from date of issue.',
      mockupStyle: 'modern-teal'
    },
    {
      id: 'minimal-clean',
      name: 'Minimal Clean',
      description: 'No-frills minimalist template — plain text only, no logo. Ideal for doctors who prefer a clean, paperwork-style look.',
      category: 'Minimal',
      tag: 'Minimal',
      tagColor: '#64748b',
      accentColor: '#334155',
      headerType: 'text1',
      enableLogo: false,
      enableWatermark: false,
      watermarkText: '',
      watermarkOpacity: 0.08,
      enableFooter: false,
      footerText: '',
      mockupStyle: 'minimal-clean'
    },
    {
      id: 'premium-navy',
      name: 'Premium Navy',
      description: 'Structured table-based header with vertical divider between logo and clinic details. Sophisticated look for high-end hospitals and specialists.',
      category: 'Premium',
      tag: 'Premium',
      tagColor: '#4338ca',
      accentColor: '#1e1b4b',
      headerType: 'text2',
      enableLogo: true,
      enableWatermark: true,
      watermarkText: 'CONFIDENTIAL',
      watermarkOpacity: 0.06,
      enableFooter: true,
      footerText: 'This prescription is computer generated and valid without signature.',
      mockupStyle: 'premium-navy'
    },
    {
      id: 'warm-clinic',
      name: 'Warm Clinic',
      description: 'Warm earthy tones with a friendly single-column text style. Great for family medicine and general practices with a personal touch.',
      category: 'Classic',
      tag: 'Classic',
      tagColor: '#92400e',
      accentColor: '#b45309',
      headerType: 'text1',
      enableLogo: true,
      enableWatermark: false,
      watermarkText: '',
      watermarkOpacity: 0.08,
      enableFooter: true,
      footerText: 'Take care and feel better soon! For queries call the clinic.',
      mockupStyle: 'warm-clinic'
    },
    {
      id: 'pediatric-soft',
      name: 'Pediatric Soft',
      description: 'Light, soft styling with rounded card header. Designed for pediatric clinics to feel friendly and approachable.',
      category: 'Pediatric',
      tag: 'Specialty',
      tagColor: '#7c3aed',
      accentColor: '#7c3aed',
      headerType: 'text1',
      enableLogo: true,
      enableWatermark: false,
      watermarkText: '',
      watermarkOpacity: 0.08,
      enableFooter: true,
      footerText: 'Paediatric dosage prescribed. Do not self-medicate.',
      mockupStyle: 'pediatric-soft'
    },
    {
      id: 'bold-header',
      name: 'Bold Header',
      description: 'Full-width header with uppercase clinic name and double underline separator. Strong visual identity for brand-conscious practices.',
      category: 'Modern',
      tag: 'Bold',
      tagColor: '#dc2626',
      accentColor: '#dc2626',
      headerType: 'text2',
      enableLogo: true,
      enableWatermark: true,
      watermarkText: 'ORIGINAL',
      watermarkOpacity: 0.07,
      enableFooter: true,
      footerText: 'Consult your doctor before changing medication.',
      mockupStyle: 'bold-header'
    },
    {
      id: 'executive-dark',
      name: 'Executive Dark',
      description: 'High-contrast dark-accent banner with inverted logo section. Ultra-premium feel for executive health clinics and top specialists.',
      category: 'Premium',
      tag: 'Exclusive',
      tagColor: '#1c1917',
      accentColor: '#292524',
      headerType: 'text2',
      enableLogo: true,
      enableWatermark: true,
      watermarkText: 'PRIVATE & CONFIDENTIAL',
      watermarkOpacity: 0.05,
      enableFooter: true,
      footerText: 'This document is private and intended for the patient only.',
      mockupStyle: 'executive-dark'
    },
    {
      id: 'green-wellness',
      name: 'Green Wellness',
      description: 'Calming green palette representing health and vitality. Suitable for wellness clinics, ayurveda, and holistic medicine practices.',
      category: 'Minimal',
      tag: 'Wellness',
      tagColor: '#15803d',
      accentColor: '#166534',
      headerType: 'text1',
      enableLogo: true,
      enableWatermark: false,
      watermarkText: '',
      watermarkOpacity: 0.08,
      enableFooter: true,
      footerText: 'Nature heals — follow the regimen for best results.',
      mockupStyle: 'green-wellness'
    }
  ];

  selected_id: any;
  hospital_id: any;
  isPageLoading = true;

  constructor(
    private toast: ToastrService,
    private clientService: ClientService,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.hospital_id = this.localStorageService.getHospitalId();
    this.loadSettings();
  }

  loadSettings() {
    // Robust truthy check — server may store 'true', '1', true (bool), etc.
    const isTrue = (val: any) => {
      return val === 'true' || val === '1' || val === 'yes' || val === true;
    };

    // Load initial fallbacks from localStorage
    this.enablePrintLayout   = isTrue(localStorage.getItem('enablePrintLayout'));
    this.prescriptionHeaderType = localStorage.getItem('prescriptionHeaderType') || 'text1';
    this.prescriptionAccentColor = localStorage.getItem('prescriptionAccentColor') || '#0d4259';

    this.presc_doc_name      = localStorage.getItem('presc_doc_name') || '';
    this.presc_doc_degree    = localStorage.getItem('presc_doc_degree') || '';
    this.presc_doc_reg       = localStorage.getItem('presc_doc_reg') || '';
    this.presc_clinic_name   = localStorage.getItem('presc_clinic_name') || '';
    this.presc_clinic_address = localStorage.getItem('presc_clinic_address') || '';
    this.presc_clinic_phone  = localStorage.getItem('presc_clinic_phone') || '';

    this.headerImageBase64   = localStorage.getItem('getBase64HeaderPrescription') || '';

    this.enablePrescriptionLogo   = isTrue(localStorage.getItem('enablePrescriptionLogo'));
    this.prescriptionLogoBase64   = localStorage.getItem('prescriptionLogoBase64') || '';

    this.enablePrescriptionWatermark = isTrue(localStorage.getItem('enablePrescriptionWatermark'));
    this.prescriptionWatermarkText   = localStorage.getItem('prescriptionWatermarkText') || '';
    this.prescriptionWatermarkOpacity = parseFloat(localStorage.getItem('prescriptionWatermarkOpacity') || '0.08');

    this.enablePrescriptionFooter = isTrue(localStorage.getItem('enablePrescriptionFooter'));
    this.prescriptionFooterText   = localStorage.getItem('prescriptionFooterText') || '';
    this.appliedTemplateId        = localStorage.getItem('appliedPrescriptionTemplateId') || null;

    // Fetch from database
    const payload = {
      where: { hospital_id: this.hospital_id },
      order: { col: '', dir: '' }
    };

    this.clientService.getAllClientConfig(payload).subscribe({
      next: (res: any) => {
        if (res && res.rows && res.rows.length > 0) {
          const configRecord = res.rows[0];
          this.selected_id = configRecord.id;
          
          if (configRecord.prescription_config) {
            const pc = configRecord.prescription_config;
            
            this.enablePrintLayout   = isTrue(pc.enablePrintLayout);
            this.prescriptionHeaderType = pc.prescriptionHeaderType || 'text1';
            this.prescriptionAccentColor = pc.prescriptionAccentColor || '#0d4259';
            this.presc_doc_name      = pc.presc_doc_name || '';
            this.presc_doc_degree    = pc.presc_doc_degree || '';
            this.presc_doc_reg       = pc.presc_doc_reg || '';
            this.presc_clinic_name   = pc.presc_clinic_name || '';
            this.presc_clinic_address = pc.presc_clinic_address || '';
            this.presc_clinic_phone  = pc.presc_clinic_phone || '';
            this.headerImageBase64   = pc.headerImageBase64 || '';
            this.enablePrescriptionLogo   = isTrue(pc.enablePrescriptionLogo);
            this.prescriptionLogoBase64   = pc.prescriptionLogoBase64 || '';
            this.enablePrescriptionWatermark = isTrue(pc.enablePrescriptionWatermark);
            this.prescriptionWatermarkText   = pc.prescriptionWatermarkText || '';
            this.prescriptionWatermarkOpacity = parseFloat(pc.prescriptionWatermarkOpacity || '0.08');
            this.enablePrescriptionFooter = isTrue(pc.enablePrescriptionFooter);
            this.prescriptionFooterText   = pc.prescriptionFooterText || '';
            this.appliedTemplateId        = pc.appliedTemplateId || null;

            // Sync with localStorage for templates and printing services compatibility
            localStorage.setItem('enablePrintLayout', this.enablePrintLayout ? 'true' : 'false');
            localStorage.setItem('prescriptionHeaderType', this.prescriptionHeaderType);
            localStorage.setItem('prescriptionAccentColor', this.prescriptionAccentColor);
            localStorage.setItem('presc_doc_name', this.presc_doc_name);
            localStorage.setItem('presc_doc_degree', this.presc_doc_degree);
            localStorage.setItem('presc_doc_reg', this.presc_doc_reg);
            localStorage.setItem('presc_clinic_name', this.presc_clinic_name);
            localStorage.setItem('presc_clinic_address', this.presc_clinic_address);
            localStorage.setItem('presc_clinic_phone', this.presc_clinic_phone);
            localStorage.setItem('getBase64HeaderPrescription', this.headerImageBase64);
            localStorage.setItem('enablePrescriptionLogo', this.enablePrescriptionLogo ? 'true' : 'false');
            localStorage.setItem('prescriptionLogoBase64', this.prescriptionLogoBase64);
            localStorage.setItem('enablePrescriptionWatermark', this.enablePrescriptionWatermark ? 'true' : 'false');
            localStorage.setItem('prescriptionWatermarkText', this.prescriptionWatermarkText);
            localStorage.setItem('prescriptionWatermarkOpacity', this.prescriptionWatermarkOpacity.toString());
            localStorage.setItem('enablePrescriptionFooter', this.enablePrescriptionFooter ? 'true' : 'false');
            localStorage.setItem('prescriptionFooterText', this.prescriptionFooterText);
            if (this.appliedTemplateId) {
              localStorage.setItem('appliedPrescriptionTemplateId', this.appliedTemplateId);
            } else {
              localStorage.removeItem('appliedPrescriptionTemplateId');
            }
          }
        }
        this.isPageLoading = false;
        
        // Auto-fill clinic fields from login data if not set
        if (!this.presc_doc_name)      this.presc_doc_name      = localStorage.getItem('profile_name') || '';
        if (!this.presc_clinic_name)   this.presc_clinic_name   = localStorage.getItem('hospital_name') || '';
        if (!this.presc_clinic_address) this.presc_clinic_address = localStorage.getItem('hospital_address') || '';
        if (!this.presc_clinic_phone)  this.presc_clinic_phone  = localStorage.getItem('hospital_phone') || '';
      },
      error: (err) => {
        console.error('Failed to load prescription settings from DB', err);
        this.isPageLoading = false;
        
        // Auto-fill clinic fields from login data if not set
        if (!this.presc_doc_name)      this.presc_doc_name      = localStorage.getItem('profile_name') || '';
        if (!this.presc_clinic_name)   this.presc_clinic_name   = localStorage.getItem('hospital_name') || '';
        if (!this.presc_clinic_address) this.presc_clinic_address = localStorage.getItem('hospital_address') || '';
        if (!this.presc_clinic_phone)  this.presc_clinic_phone  = localStorage.getItem('hospital_phone') || '';
      }
    });
  }

  onImageUpload(event: any) {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.toast.error('Only image files are supported');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { this.headerImageBase64 = reader.result as string; };
    reader.onerror = () => this.toast.error('Failed to read image file');
    reader.readAsDataURL(file);
  }

  removeHeaderImage() { this.headerImageBase64 = ''; }

  onLogoUpload(event: any) {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.toast.error('Only image files are supported');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { this.prescriptionLogoBase64 = reader.result as string; };
    reader.onerror = () => this.toast.error('Failed to read logo file');
    reader.readAsDataURL(file);
  }

  removeLogo() { this.prescriptionLogoBase64 = ''; }

  selectHeaderType(type: string) {
    this.prescriptionHeaderType = type;
    this.onCustomSettingChanged();
  }

  /** Called whenever user manually changes any custom setting — clears any applied template */
  onCustomSettingChanged() {
    if (this.appliedTemplateId) {
      this.appliedTemplateId = null;
      localStorage.removeItem('appliedPrescriptionTemplateId');
    }
  }

  /** Clear applied template and switch to custom mode */
  clearTemplate() {
    this.appliedTemplateId = null;
    localStorage.removeItem('appliedPrescriptionTemplateId');
    this.activeTab = 'custom';
    this.saveSettingsToDb(false);
    this.toast.info('Template cleared. You are now in Custom Mode.');
  }

  saveSettingsToDb(showToast = true) {
    const prescriptionConfig = {
      enablePrintLayout: this.enablePrintLayout,
      prescriptionHeaderType: this.prescriptionHeaderType,
      prescriptionAccentColor: this.prescriptionAccentColor,
      presc_doc_name: this.presc_doc_name,
      presc_doc_degree: this.presc_doc_degree,
      presc_doc_reg: this.presc_doc_reg,
      presc_clinic_name: this.presc_clinic_name,
      presc_clinic_address: this.presc_clinic_address,
      presc_clinic_phone: this.presc_clinic_phone,
      headerImageBase64: this.headerImageBase64,
      enablePrescriptionLogo: this.enablePrescriptionLogo,
      prescriptionLogoBase64: this.prescriptionLogoBase64,
      enablePrescriptionWatermark: this.enablePrescriptionWatermark,
      prescriptionWatermarkText: this.prescriptionWatermarkText,
      prescriptionWatermarkOpacity: this.prescriptionWatermarkOpacity,
      enablePrescriptionFooter: this.enablePrescriptionFooter,
      prescriptionFooterText: this.prescriptionFooterText,
      appliedTemplateId: this.appliedTemplateId
    };

    const payload = {
      hospital_id: this.hospital_id,
      prescription_config: prescriptionConfig,
      id: this.selected_id
    };

    const apiCall = this.selected_id
      ? this.clientService.updateClientConfig(payload)
      : this.clientService.createClientConfig(payload);

    apiCall.subscribe({
      next: (res: any) => {
        if (res && res.id) {
          this.selected_id = res.id;
        }
        if (showToast) {
          this.toast.success('Prescription layout settings saved! Print a prescription to see the result.');
        }
      },
      error: (err) => {
        console.error('Failed to save prescription settings to DB', err);
        if (showToast) {
          this.toast.error('Failed to save settings to database.');
        }
      }
    });
  }

  saveSettings() {
    // ── ALWAYS force enablePrintLayout ON when saving any custom settings.
    // The toggle is now only used to DISABLE the layout, never to gate the save.
    this.enablePrintLayout = true;

    // Auto-enable logo if a logo image is already uploaded
    if (this.prescriptionLogoBase64 && !this.enablePrescriptionLogo) {
      this.enablePrescriptionLogo = true;
    }

    localStorage.setItem('enablePrintLayout', 'true');
    localStorage.setItem('prescriptionHeaderType', this.prescriptionHeaderType);
    localStorage.setItem('prescriptionAccentColor', this.prescriptionAccentColor);
    localStorage.setItem('presc_doc_name', this.presc_doc_name);
    localStorage.setItem('presc_doc_degree', this.presc_doc_degree);
    localStorage.setItem('presc_doc_reg', this.presc_doc_reg);
    localStorage.setItem('presc_clinic_name', this.presc_clinic_name);
    localStorage.setItem('presc_clinic_address', this.presc_clinic_address);
    localStorage.setItem('presc_clinic_phone', this.presc_clinic_phone);
    localStorage.setItem('getBase64HeaderPrescription', this.headerImageBase64);
    localStorage.setItem('enablePrescriptionLogo', this.enablePrescriptionLogo ? 'true' : 'false');
    localStorage.setItem('prescriptionLogoBase64', this.prescriptionLogoBase64);
    localStorage.setItem('enablePrescriptionWatermark', this.enablePrescriptionWatermark ? 'true' : 'false');
    localStorage.setItem('prescriptionWatermarkText', this.prescriptionWatermarkText);
    localStorage.setItem('prescriptionWatermarkOpacity', this.prescriptionWatermarkOpacity.toString());
    localStorage.setItem('enablePrescriptionFooter', this.enablePrescriptionFooter ? 'true' : 'false');
    localStorage.setItem('prescriptionFooterText', this.prescriptionFooterText);
    
    this.saveSettingsToDb(true);
  }

  // -------------------------------------------------------
  //  Template Library logic
  // -------------------------------------------------------

  get filteredTemplates(): PrescriptionTemplate[] {
    let list = this.templateLibrary;
    if (this.selectedCategory !== 'All') {
      list = list.filter(t => t.category === this.selectedCategory);
    }
    if (this.templateSearchQuery.trim()) {
      const q = this.templateSearchQuery.toLowerCase();
      list = list.filter(t =>
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q) ||
        t.tag.toLowerCase().includes(q)
      );
    }
    return list;
  }

  applyTemplate(tpl: PrescriptionTemplate) {
    // ── Resolve clinic info: use saved custom fields, else fall back to login data ──
    const clinicName    = this.presc_clinic_name   || localStorage.getItem('hospital_name')    || '';
    const clinicAddress = this.presc_clinic_address || localStorage.getItem('hospital_address') || '';
    const clinicPhone   = this.presc_clinic_phone   || localStorage.getItem('hospital_phone')   || '';

    // ── Update component state ──
    this.enablePrintLayout           = true;
    this.prescriptionHeaderType      = tpl.headerType;
    this.prescriptionAccentColor     = tpl.accentColor;
    this.enablePrescriptionLogo      = tpl.enableLogo;
    this.enablePrescriptionWatermark = tpl.enableWatermark;
    this.prescriptionWatermarkText   = tpl.watermarkText;
    this.prescriptionWatermarkOpacity = tpl.watermarkOpacity;
    this.enablePrescriptionFooter    = tpl.enableFooter;
    this.prescriptionFooterText      = tpl.footerText;
    this.appliedTemplateId           = tpl.id;
    this.presc_clinic_name           = clinicName;
    this.presc_clinic_address        = clinicAddress;
    this.presc_clinic_phone          = clinicPhone;

    // ── Write DIRECTLY to localStorage — no dependency on stale component state ──
    localStorage.setItem('enablePrintLayout',           'true');
    localStorage.setItem('prescriptionHeaderType',      tpl.headerType);
    localStorage.setItem('prescriptionAccentColor',     tpl.accentColor);
    localStorage.setItem('enablePrescriptionLogo',      tpl.enableLogo    ? 'true' : 'false');
    localStorage.setItem('enablePrescriptionWatermark', tpl.enableWatermark ? 'true' : 'false');
    localStorage.setItem('prescriptionWatermarkText',   tpl.watermarkText);
    localStorage.setItem('prescriptionWatermarkOpacity', tpl.watermarkOpacity.toString());
    localStorage.setItem('enablePrescriptionFooter',    tpl.enableFooter  ? 'true' : 'false');
    localStorage.setItem('prescriptionFooterText',      tpl.footerText);
    localStorage.setItem('appliedPrescriptionTemplateId', tpl.id);

    // Save resolved clinic info so the prescription service picks it up
    localStorage.setItem('presc_clinic_name',    clinicName);
    localStorage.setItem('presc_clinic_address', clinicAddress);
    localStorage.setItem('presc_clinic_phone',   clinicPhone);

    console.log('[PrescriptionConfig] Template applied:', tpl.name, {
      headerType: tpl.headerType,
      accentColor: tpl.accentColor,
      enableLayout: 'true',
      clinicName,
      clinicAddress,
      clinicPhone
    });

    this.saveSettingsToDb(false);
    this.toast.success(`"${tpl.name}" template applied! Open any prescription and print to see it.`);
  }

  isApplied(tpl: PrescriptionTemplate): boolean {
    return this.appliedTemplateId === tpl.id;
  }

  getAppliedTemplateName(): string {
    if (!this.appliedTemplateId) return '';
    const tpl = this.templateLibrary.find(t => t.id === this.appliedTemplateId);
    return tpl ? tpl.name : '';
  }

  setTab(tab: 'custom' | 'templates') {
    this.activeTab = tab;
  }
}
